import { useRef } from 'react';
import { usePublicStoreFeaturesVisible } from '../../shell/feature-flags/usePublicStoreFeaturesVisible';
import { useHasBusinessCapability } from '../../shell/session/hooks/useEffectiveBusinessCapabilities';
import type { useOnlineOperatingSetup } from './useOnlineOperatingSetup';
import type { useWeekOneSetup } from './useWeekOneSetup';
import type { StoreActivationEvidence } from './useStoreActivationEvidence';

type WeekOneSetupState = ReturnType<typeof useWeekOneSetup>;
type OnlineOperatingState = ReturnType<typeof useOnlineOperatingSetup>;

type Params = {
  userId: string | undefined;
  isOwner: boolean;
  offerAllowed: boolean;
  weekOne: WeekOneSetupState;
  onlineOperating: OnlineOperatingState;
  evidence: StoreActivationEvidence;
};

/**
 * True when B3 or B4 home cards would render — B5 weekly card must stay hidden.
 * weekOne / onlineOperating / evidence vienen del provider (una sola instancia
 * por Inicio; la evidencia remota se carga una vez en useStoreActivationEvidence).
 */
export function useHomeOnboardingCardsActive({
  userId,
  isOwner,
  offerAllowed,
  weekOne,
  onlineOperating,
  evidence,
}: Params) {
  const publicStoreOn = usePublicStoreFeaturesVisible();
  const canBranch = useHasBusinessCapability('employees');
  const reloadInFlightRef = useRef(false);

  // Flags locales vienen de la evidencia compartida (una lectura por mount).
  // Default "dismissed" hasta que cargan — mismo comportamiento que antes.
  const weekOneDismissed = evidence.flags?.weekOneDismissed ?? true;
  const storeDismissed = evidence.flags?.storeDismissed ?? true;
  const onlineDismissed = evidence.flags?.onlineDismissed ?? true;
  // Activación completa ⇔ tienda publicada (home_snapshot.storefront).
  const storeAllDone = evidence.isPublic === true;

  const weekOneWouldShow =
    isOwner &&
    offerAllowed &&
    canBranch &&
    weekOne.eligible &&
    !weekOne.allDone &&
    !weekOneDismissed;

  const storeActivationWouldShow =
    isOwner &&
    offerAllowed &&
    canBranch &&
    publicStoreOn &&
    !storeDismissed &&
    !storeAllDone;

  const onlineOperatingWouldShow =
    isOwner &&
    offerAllowed &&
    canBranch &&
    publicStoreOn &&
    !storeActivationWouldShow &&
    onlineOperating.eligible &&
    !onlineOperating.allDone &&
    !onlineDismissed;

  const blocking = weekOneWouldShow || storeActivationWouldShow || onlineOperatingWouldShow;

  return {
    blocking,
    weekOneWouldShow,
    storeActivationWouldShow,
    onlineOperatingWouldShow,
    loading: evidence.loading,
    reload: async () => {
      if (reloadInFlightRef.current) return;
      reloadInFlightRef.current = true;
      try {
        await evidence.reload({ force: true });
      } finally {
        reloadInFlightRef.current = false;
      }
    },
  };
}
